// Authentication state management using Zustand
import { create } from 'zustand';
import {
  User,
  LoginCredentials,
  RegisterData,
  AuthProvider,
  OrySessionInfo,
  AssignedSalespersonSummary,
} from '@types';
import { authService, oryService } from '@services';
import { useApplicationStore } from './applicationStore';
import { resetMfaLoginState } from '../services/auth/mfa-login.service';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  authProvider: AuthProvider;
  orySession: OrySessionInfo | null;

  // Legacy auth methods
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;

  // Ory auth methods
  initiateOryLogin: () => Promise<void>;
  initiateOryRegistration: () => Promise<void>;
  handleOryCallback: () => Promise<void>;
  syncOrySession: () => Promise<void>;

  // Common methods
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  clearError: () => void;
  setAssignedSalesperson: (
    assignedSalespersonId: string | null,
    assignedSalesperson: AssignedSalespersonSummary | null
  ) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: authService.getStoredUser(),
  isAuthenticated: authService.isAuthenticated(),
  // Route guards must wait for the first Ory cookie/session check. Otherwise a full-page load can
  // briefly render /login and create a second login flow for an already authenticated browser.
  isLoading: true,
  error: null,
  authProvider: null,
  orySession: null,

  // Legacy login (kept for backward compatibility)
  login: async (credentials: LoginCredentials) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login(credentials);
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        authProvider: 'legacy',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Legacy register (kept for backward compatibility)
  register: async (data: RegisterData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.register(data);
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        authProvider: 'legacy',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Initiate Ory login flow (redirects to Ory UI)
  initiateOryLogin: async () => {
    set({ isLoading: true, error: null });
    try {
      await oryService.createLoginFlow();
      // User will be redirected, so we don't update state here
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initiate login';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Initiate Ory registration flow (redirects to Ory UI)
  initiateOryRegistration: async () => {
    set({ isLoading: true, error: null });
    try {
      await oryService.createRegistrationFlow();
      // User will be redirected, so we don't update state here
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to initiate registration';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Handle callback after Ory flow completion
  handleOryCallback: async () => {
    set({ isLoading: true, error: null });
    try {
      const sessionInfo = await oryService.getSession();

      if (!sessionInfo || !sessionInfo.isActive) {
        throw new Error('No active session found');
      }

      const user = await oryService.getCurrentUser();

      if (!user) {
        throw new Error('Failed to get user information');
      }

      set({
        user,
        isAuthenticated: sessionInfo.session.authenticator_assurance_level === 'aal2',
        isLoading: false,
        authProvider: 'ory',
        orySession: sessionInfo,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      set({ error: errorMessage, isLoading: false, isAuthenticated: false });
      throw error;
    }
  },

  // Sync Ory session (called on app load)
  syncOrySession: async () => {
    set({ isLoading: true, error: null });
    try {
      console.log('Syncing Ory session...');
      const sessionInfo = await oryService.getSession();
      console.log('Ory session info:', sessionInfo);

      if (sessionInfo && sessionInfo.isActive) {
        const assuranceLevel = sessionInfo.session.authenticator_assurance_level;

        // Preserve an AAL1 session so /login can continue MFA, but do not expose protected routes
        // or create another password flow for a browser that is already partway through login.
        if (assuranceLevel !== 'aal2') {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            authProvider: 'ory',
            orySession: sessionInfo,
          });
          return;
        }

        const user = await oryService.getCurrentUser();
        console.log('Ory user:', user);

        if (user) {
          console.log('[authStore.syncOrySession] resolved user=', user);
          console.log('Setting auth provider to ORY');
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            authProvider: 'ory',
            orySession: sessionInfo,
          });
          return;
        }
      }

      // No Ory session, check for legacy token (only in development)
      const isProduction = import.meta.env.VITE_ENV === 'production';

      if (!isProduction) {
        console.log('No Ory session found, checking legacy auth...');
        if (authService.isAuthenticated()) {
          console.log('Setting auth provider to LEGACY');
          set({
            user: authService.getStoredUser(),
            isAuthenticated: true,
            isLoading: false,
            authProvider: 'legacy',
          });
          return;
        }
      }

      console.log('No authentication found');
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        authProvider: null,
      });
    } catch (error) {
      console.error('Error syncing Ory session:', error);

      // Failed to sync Ory session, check legacy auth (only in development)
      const isProduction = import.meta.env.VITE_ENV === 'production';

      if (!isProduction && authService.isAuthenticated()) {
        console.log('Error fallback: Setting auth provider to LEGACY');
        set({
          user: authService.getStoredUser(),
          isAuthenticated: true,
          isLoading: false,
          authProvider: 'legacy',
        });
      } else {
        console.log('Error fallback: No authentication');
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          authProvider: null,
          error: null, // Don't show error on initial sync failure
        });
      }
    }
  },

  // Logout (works with both Ory and legacy)
  logout: async () => {
    resetMfaLoginState();
    set({ isLoading: true, error: null });
    const { authProvider } = get();

    // In production, always use Ory logout
    // In development, check authProvider (legacy might still be used)
    const isProduction = import.meta.env.VITE_ENV === 'production';

    try {
      if (authProvider === 'ory' || isProduction) {
        // For Ory logout: Do NOT clear state before redirect
        // The Ory service will redirect to Ory's logout endpoint
        // which will clear the Ory session and redirect back to login
        // We clear application data here to ensure it's removed before redirect
        const applicationStore = useApplicationStore.getState();
        applicationStore.resetApplication();

        // This will redirect and not return
        await oryService.logout();
        // Note: Code after this won't execute as browser redirects
      } else {
        // Legacy logout (only in development)
        // Clear application data before logout
        const applicationStore = useApplicationStore.getState();
        applicationStore.resetApplication();

        await authService.logout();
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          authProvider: null,
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Logout failed';
      set({ error: errorMessage, isLoading: false });

      // Even if logout fails, clear local state
      const applicationStore = useApplicationStore.getState();
      applicationStore.resetApplication();

      set({
        user: null,
        isAuthenticated: false,
        authProvider: null,
        orySession: null,
      });

      // Force redirect to login even if Ory logout fails
      window.location.href = '/login';
    }
  },

  // Get current user (prioritizes Ory)
  getCurrentUser: async () => {
    set({ isLoading: true, error: null });
    const { authProvider } = get();

    try {
      if (authProvider === 'ory') {
        const user = await oryService.getCurrentUser();
        if (user) {
          set({ user, isAuthenticated: true, isLoading: false });
          return;
        }
      }

      // Fall back to legacy
      const user = await authService.getCurrentUser();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get user';
      set({ error: errorMessage, isLoading: false, isAuthenticated: false, user: null });
    }
  },

  clearError: () => set({ error: null }),
  setAssignedSalesperson: (assignedSalespersonId, assignedSalesperson) =>
    set((state) => ({
      user: state.user ? { ...state.user, assignedSalespersonId, assignedSalesperson } : state.user,
    })),
}));
